import { useCallback, useEffect, useState } from 'react';
import { app, describeError, go, leaveChildMode, resetWizard , WIZARD_SCREENS} from './state/app';
import { DEBUG } from './runtime';
import { endSession } from './auth';
import { ensureFamily, prewarm } from './api/endpoints';
import { setBalance } from './lib/devlog';
import { DevPanel } from './components/DevPanel';
import { SignIn } from './screens/SignIn';
import { Gallery } from './screens/Gallery';
import { Candidates } from './screens/Candidates';
import { NameIt } from './screens/NameIt';
import { Summary } from './screens/Summary';
import { CommissionProgress } from './screens/CommissionProgress';
import { Library } from './screens/Library';
import { Watch } from './screens/Watch';
import { ChildMode } from './screens/ChildMode';

export function App() {
  const { screen, bootError, signedInAs, childMode, householdError } = app.use();
  const [devOpen, setDevOpen] = useState(DEBUG);

  // THE CALL THAT CREATES THE HOUSEHOLD, not just a balance fetch. On a first
  // run ensureFamily() sees a 404 and POSTs /families, so a failure here is a
  // parent who is signed in with no account behind them — and it used to be
  // swallowed into the dev panel's store, which is off by default.
  const loadHousehold = useCallback(() => {
    app.set({ householdError: null });
    return ensureFamily()
      .then((me) => setBalance(me.creditBalance, me.ledger ?? []))
      .catch((err) => {
        setBalance(null, []);
        app.set({ householdError: describeError(err) });
      });
  }, []);

  useEffect(() => {
    if (!signedInAs) return;
    void loadHousehold();
    // The moment we know a parent has arrived is the moment to pay the cold
    // start on their behalf, rather than making them pay it on the library.
    // See prewarm()'s note for what it does and does not promise.
    prewarm();
  }, [signedInAs, loadHousehold]);

  if (bootError) {
    return (
      <div className="centered">
        <div className="panel">
          <h1>Cannot boot</h1>
          <div className="err">{bootError.message}</div>
          {bootError.configFault ? (
            // Only said when loadConfig() is what failed. This paragraph used
            // to be printed under EVERY boot failure, including a cancelled
            // Entra sign-in — which is not a deployment problem at all, and
            // does not reach here any more precisely because it is not one.
            <p className="sub">
              /config.json ships in web/public/ and is edited in place at deploy
              time with the values azure/entra_bootstrap.sh prints. Locally, copy
              config.local.json.example to config.local.json.
            </p>
          ) : (
            <>
              <p className="sub">
                Something went wrong before the app could start. Signing out
                clears anything left over from a previous visit, which is the one
                thing this page can usefully do.
              </p>
              {/* THE WAY OUT. There was none: this panel was the whole app, and
                  the only control that clears a stale session lives on the top
                  bar, which is behind it. A parent who reached this state could
                  not leave it by reloading — the stale account was still there
                  and put them straight back. */}
              <button
                className="big"
                onClick={() => {
                  void endSession().finally(() => {
                    resetWizard();
                    // A hand-over lock can outlive a boot failure now that it is
                    // stored: this panel renders AHEAD of the child shell, so a
                    // parent could clear the error, sign back in, and land in
                    // their own child mode behind a PIN they set an hour ago.
                    leaveChildMode();
                    app.set({
                      bootError: null,
                      signedInAs: null,
                      signInNotice: null,
                    });
                    // go(), not `screen` in the patch above: screens have URLs
                    // now, and the address bar has to stop naming whatever deep
                    // link this panel appeared over.
                    go('signin');
                  });
                }}
              >
                Sign out and start again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (screen === 'boot') {
    return (
      <div className="centered">
        <div className="panel pulse">
          <h1>Showling</h1>
          <p className="sub">reading /config.json…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="layout">
      <div className="stage">
        {screen === 'signin' ? (
          <SignIn />
        ) : childMode ? (
          // Child mode replaces the whole shell, top bar included: the point is
          // that there is nothing else on screen to press.
          <div className="ipad">
            <div className="screen">
              <ChildMode />
            </div>
            <div className="homebar" />
          </div>
        ) : (
          <div className="ipad">
            <div className="screen">
              <TopBar />
              {householdError && <HouseholdError message={householdError} onRetry={loadHousehold} />}
              {screen === 'gallery' && <Gallery />}
              {screen === 'candidates' && <Candidates />}
              {screen === 'name' && <NameIt />}
              {screen === 'summary' && <Summary />}
              {screen === 'commission' && <CommissionProgress />}
              {screen === 'library' && <Library />}
              {screen === 'watch' && <Watch />}
            </div>
            <div className="homebar" />
          </div>
        )}
      </div>

      {devOpen && DEBUG && <DevPanel onClose={() => setDevOpen(false)} />}
      {!devOpen && DEBUG && (
        <button className="dev-toggle" onClick={() => setDevOpen(true)}>
          dev
        </button>
      )}
    </div>
  );
}

/**
 * Rendered in the SHELL rather than on a screen, because the household is what
 * every screen below it assumes exists. A parent who cannot be told this is a
 * parent looking at an empty gallery and concluding the product is broken.
 */
function HouseholdError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    // A BANNER ACROSS THE TOP, not a third column. `.screen` is a flex ROW, so
    // this block sat beside the picture pane and the question pane as an equal
    // flex item: it took the left third floor-to-ceiling and squeezed the whole
    // gallery into what was left — 50px tiles captioned "Purple unicor",
    // "Ginger kitte", and a describe-your-own box reading "…or des". The one
    // state that exists to explain a first run went and broke the first screen.
    <div className="err household-err" role="alert">
      <b>We could not open your account.</b>
      <div className="sub">{message}</div>
      <button type="button" className="primary" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function TopBar() {
  const { screen, signedInAs } = app.use();
  if (screen === 'watch') return null;
  return (
    <div className="topbar">
      <span>{signedInAs}</span>
      {screen !== 'library' && (
        <button onClick={() => go('library')}>library</button>
      )}
      {/* ONLY INSIDE THE WIZARD. "start over" means "throw this character away
          and begin again", which is a sentence that only parses while you are
          part-way through making one. On the library it was offering to abandon
          nothing, next to a "new character" button on the same screen that
          already does exactly this — Jamie: "I don't get why we show start over
          in top right... it's not like we're in a wizard".

          Excluding the gallery as well, because that IS the beginning: there is
          nothing yet to start over from. */}
      {WIZARD_SCREENS.has(screen) && screen !== 'gallery' && (
        <button
          onClick={() => {
            resetWizard();
            go('gallery');
          }}
        >
          start over
        </button>
      )}
      <button
        onClick={() => {
          void endSession().finally(() => {
            resetWizard();
            // Signing out ends the hand-over too. Unreachable from child mode
            // (this bar is not rendered there), but it is the one control that
            // must never leave a stored lock behind for the next visit.
            leaveChildMode();
            // signInNotice cleared with it: a parent who pressed sign out does
            // not need to be told why they are signed out.
            app.set({ signedInAs: null, signInNotice: null });
            go('signin');
          });
        }}
      >
        sign out
      </button>
    </div>
  );
}
